'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { UserPlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import { createClient } from '@/lib/local-api/client';
import { loadInteractions } from '@/lib/local-api/interaction-cache';

interface SuggestedPerson {
  id: string;
  full_name: string;
  username?: string;
  avatar_url?: string;
}

function getInitials(person: SuggestedPerson) {
  const name = person.full_name?.trim() || person.username?.trim() || 'Member';
  const parts = name.split(/\s+/);
  const first = Array.from(parts[0])[0] || 'M';
  const last = parts.length > 1 ? Array.from(parts[parts.length - 1])[0] || '' : '';
  return (first + last).toUpperCase();
}

function PersonAvatar({ person }: { person: SuggestedPerson }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  return (
    <div className="mx-auto mb-3 flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-xl font-bold leading-none text-white shadow-sm">
      {person.avatar_url && person.avatar_url !== failedUrl ? (
        <img src={person.avatar_url} alt={person.full_name || 'Member'} loading="lazy"
          onError={() => setFailedUrl(person.avatar_url ?? null)} className="h-full w-full object-cover" />
      ) : <span aria-hidden="true">{getInitials(person)}</span>}
    </div>
  );
}

interface Props {
  currentUserId: string;
  connections: Set<string>;
  sentRequests: Set<string>;
  incomingRequests: { sender_id: string }[];
  processingRequest: string | null;
  onSendRequest: (id: string) => Promise<void>;
  onSeeAll: () => void;
}

export default function PeopleYouMayKnow({ currentUserId, connections, sentRequests, incomingRequests, processingRequest, onSendRequest, onSeeAll }: Props) {
  const [suggestions, setSuggestions] = useState<{ userId: string; people: SuggestedPerson[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const client = createClient();

    async function load() {
      try {
        const [profiles, interactions] = await Promise.all([
          client.from('user_profiles')
            .select('id, full_name, username, avatar_url')
            .neq('id', currentUserId)
            .limit(100),
          loadInteractions(currentUserId, client),
        ]);
        if (cancelled || profiles.error) return;
        const excluded = new Set([
          ...interactions.blocks.map((row) => row.blocked_id),
          ...interactions.mutes.map((row) => row.muted_id),
        ]);
        const people = ((profiles.data ?? []) as SuggestedPerson[]).filter((person) => !excluded.has(person.id));
        // Shuffle once per visit so ordinary feed updates do not reorder the cards.
        for (let index = people.length - 1; index > 0; index--) {
          const randomIndex = Math.floor(Math.random() * (index + 1));
          [people[index], people[randomIndex]] = [people[randomIndex], people[index]];
        }
        setSuggestions({ userId: currentUserId, people });
      } catch {
        // Suggestions are optional; the post feed remains usable if loading fails.
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [currentUserId]);

  const people = (suggestions?.userId === currentUserId ? suggestions.people : [])
    .filter((person) => person.id !== currentUserId && !connections.has(person.id) && !sentRequests.has(person.id)
      && !incomingRequests.some((request) => request.sender_id === person.id))
    .slice(0, 4);

  if (!people.length) return null;

  return (
    <section aria-label="People you may know" className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <h2 className="flex items-center gap-2 text-sm font-bold text-foreground">
          <UsersIcon className="w-5 h-5 text-[#1877f2]" />
          People you may know
        </h2>
        <button type="button" onClick={onSeeAll} className="text-xs font-semibold text-[#1877f2] hover:underline whitespace-nowrap">See all</button>
      </div>
      <div className="flex gap-3 overflow-x-auto p-4 snap-x">
        {people.map((person) => (
          <div key={person.id} className="w-36 shrink-0 snap-start rounded-xl border border-border p-3 text-center sm:flex-1">
            <Link href={`/community-network/profile/${person.id}`} className="block">
              <PersonAvatar person={person} />
              <p className="truncate text-sm font-semibold text-foreground hover:text-[#1877f2]">{person.full_name || 'Member'}</p>
              {/* <p className="h-5 truncate text-xs text-muted-foreground">{person.username ? `@${person.username}` : 'Community member'}</p> */}
            </Link>
            <button type="button" onClick={() => void onSendRequest(person.id)} disabled={processingRequest !== null}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#1877f2]/10 px-2 py-2 text-xs font-semibold text-[#1877f2] hover:bg-[#1877f2]/20 disabled:opacity-50">
              <UserPlusIcon className="w-4 h-4" />
              {processingRequest === person.id ? 'Sending…' : 'Add friend'}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
